import { usersApiClient } from './api';

export interface BiblicalRank {
  level: number;
  name: string;
  pointsRequired: number;
  tier: string;
}

export interface RankProgress {
  currentRank: BiblicalRank;
  nextRank: BiblicalRank | null;
  progressPercentage: number;
  progressText: string;
  pointsToNextRank: number;
  pointsIntoCurrentRank: number;
  isMaxRank: boolean;
}

export interface RankLore {
  name: string;
  tierColor: string;
  openingQuote: string;
  mainDescription: string[];
  biblicalExampleTitle: string;
  biblicalExampleDescription: string;
  emoji: string;
}

// Rank image mapping
export const getRankImagePath = (rankName: string): string => {
  const imageMap: { [key: string]: string } = {
    'Seeker': require('../assets/ranks/seekerrank.png'),
    'Believer': require('../assets/ranks/believer.png'),
    'Follower': require('../assets/ranks/follower.png'),
    'Servant': require('../assets/ranks/servant.png'),
    'Shepherd': require('../assets/ranks/shepherd.png'),
    'Elder': require('../assets/ranks/elder.png'),
    'Prophet': require('../assets/ranks/prophet.png'),
    'Disciple': require('../assets/ranks/disciple.png'),
    'Apostle': require('../assets/ranks/apostle.png'),
    'Evangelist': require('../assets/ranks/evangelist.png'),
    'Teacher': require('../assets/ranks/teacher.png'),
    'Theologian': require('../assets/ranks/theologian.png'),
    'Scholar': require('../assets/ranks/scholar.png'),
    'Sage': require('../assets/ranks/sage.png'),
    'Biblical Master': require('../assets/ranks/biblicalmaster.png'),
  };
  
  return imageMap[rankName] || require('../assets/ranks/seekerrank.png');
};

// Rank emoji mapping
export const getRankEmoji = (rankName: string): string => {
  const emojiMap: { [key: string]: string } = {
    'Seeker': '🔍',
    'Believer': '🙏',
    'Follower': '🚶',
    'Servant': '🤝',
    'Shepherd': '🐑',
    'Elder': '👴',
    'Prophet': '📜',
    'Disciple': '✝️',
    'Apostle': '⛪',
    'Evangelist': '📢',
    'Teacher': '👨‍🏫',
    'Theologian': '📚',
    'Scholar': '🎓',
    'Sage': '🧙‍♂️',
    'Biblical Master': '👑',
  };
  
  return emojiMap[rankName] || '⭐';
};

// Tier color mapping
export const getTierColor = (tier: string): string => {
  // Return no color - all ranks will be plain
  return '';
};

// Rank lore data
export const getRankLore = (rankName: string): RankLore => {
  const loreMap: { [key: string]: RankLore } = {
    'Seeker': {
      name: 'Seeker',
      tierColor: '#10B981',
      openingQuote: 'Every journey begins with a single step, and you have taken yours.',
      mainDescription: [
        'As a Seeker, you are like the woman who lost a silver coin and lit a lamp to search every corner of her house until she found it. Your heart is stirring with questions about God\'s Word, and you are beginning to search the Scriptures for answers.',
        'This is the foundation of all wisdom: "The fear of the Lord is the beginning of knowledge" (Proverbs 1:7). You are at the beautiful starting point where God reveals Himself to those who seek Him with their whole heart.'
      ],
      biblicalExampleTitle: 'The Ethiopian Eunuch',
      biblicalExampleDescription: 'Who was reading Isaiah but needed Philip to help him understand God\'s Word (Acts 8).',
      emoji: '🔍'
    },
    'Believer': {
      name: 'Believer',
      tierColor: '#10B981',
      openingQuote: 'You have taken the step from seeking to believing. Faith has taken root in your heart.',
      mainDescription: [
        'As a Believer, you are like the people of Berea who "received the word with all eagerness, examining the Scriptures daily to see if these things were so" (Acts 17:11). You have accepted Jesus Christ as your Savior and are beginning to understand the basics of your faith.',
        'Your belief is not just head knowledge but heart faith. You are learning that "faith comes from hearing, and hearing through the word of Christ" (Romans 10:17). Each question you answer correctly strengthens your understanding of God\'s character and His plan for humanity.'
      ],
      biblicalExampleTitle: 'The Samaritan Woman',
      biblicalExampleDescription: 'Who went from questioning Jesus to proclaiming "Come, see a man who told me everything I ever did" (John 4:29).',
      emoji: '🙏'
    },
    'Follower': {
      name: 'Follower',
      tierColor: '#10B981',
      openingQuote: 'Belief has moved you to action. You are walking the path Jesus laid out for His disciples.',
      mainDescription: [
        'As a Follower, you understand Jesus\' words: "If anyone wants to come after me, let him deny himself and take up his cross and follow me" (Matthew 16:24). You are not just learning about Jesus; you are learning to live like Him.',
        'You are beginning to grasp the depth of Jesus\' teachings and applying them to your daily life. Like the disciples who left their nets to follow Christ, you are making choices that align your life with biblical principles.'
      ],
      biblicalExampleTitle: 'Matthew the Tax Collector',
      biblicalExampleDescription: 'Who immediately left his booth when Jesus called him and followed without looking back (Matthew 9:9).',
      emoji: '🚶'
    },
    'Servant': {
      name: 'Servant',
      tierColor: '#3B82F6',
      openingQuote: 'You have embraced the heart of Jesus: "Whoever wants to be great among you must be your servant" (Matthew 20:26).',
      mainDescription: [
        'As a Servant, you understand that true greatness in God\'s kingdom comes through serving others. You see that Jesus himself "did not come to be served, but to serve, and to give his life as a ransom for many" (Mark 10:45).',
        'Your biblical knowledge is growing, and with it, your heart for others. You are learning that every gift God gives us is meant to benefit the body of Christ and glorify Him. Your service flows from love, not duty.'
      ],
      biblicalExampleTitle: 'Phoebe',
      biblicalExampleDescription: 'Who Paul called "a servant of the church" and "a helper of many" (Romans 16:1-2), faithfully serving God\'s people.',
      emoji: '🤝'
    },
    'Shepherd': {
      name: 'Shepherd',
      tierColor: '#3B82F6',
      openingQuote: 'God has given you a shepherd\'s heart to care for and guide others in their faith journey.',
      mainDescription: [
        'As a Shepherd, you reflect the heart of Jesus, the Good Shepherd who "lays down his life for the sheep" (John 10:11). You understand that shepherding is not about position but about protection, guidance, and sacrifice for those in your care.',
        'Your knowledge of Scripture has grown to where you can guide others, comfort the hurting, and protect people from false teaching. Like the shepherd who searches for the one lost sheep, you care deeply about every person God has placed in your life.'
      ],
      biblicalExampleTitle: 'Barnabas',
      biblicalExampleDescription: 'The son of encouragement, who shepherded new believers and helped restore Mark after his failure (Acts 4:36, 15:37-39).',
      emoji: '🐑'
    },
    'Elder': {
      name: 'Elder',
      tierColor: '#3B82F6',
      openingQuote: 'Wisdom and experience have marked your walk with God. Others look to you for spiritual guidance and leadership.',
      mainDescription: [
        'As an Elder, you embody Paul\'s qualifications: "above reproach, faithful, temperate, self-controlled, respectable, hospitable, able to teach" (1 Timothy 3:2). Your life has become a living example of biblical truth.',
        'You understand that with greater knowledge comes greater responsibility. Your years of studying God\'s Word have given you wisdom to counsel others, resolve conflicts, and provide spiritual oversight. Like Samuel in his old age, people trust your judgment because they see God\'s hand on your life.'
      ],
      biblicalExampleTitle: 'James, the brother of Jesus',
      biblicalExampleDescription: 'Who became a respected elder in Jerusalem and provided wise counsel at the Jerusalem Council (Acts 15).',
      emoji: '👴'
    },
    'Prophet': {
      name: 'Prophet',
      tierColor: '#8B5CF6',
      openingQuote: 'God has given you the gift to speak His truth boldly and see His hand in the events around you.',
      mainDescription: [
        'As a Prophet, you stand in the tradition of those who declared "Thus says the Lord." You have developed a deep sensitivity to God\'s voice and the courage to speak truth even when it\'s difficult to hear.',
        'Your understanding of Scripture allows you to see how God\'s eternal principles apply to current situations. Like Nathan confronting David or Deborah guiding Israel, you understand that prophecy is not just about the future but about calling people back to God\'s ways in the present.'
      ],
      biblicalExampleTitle: 'Anna the prophetess',
      biblicalExampleDescription: 'Who spent years in the temple and recognized Jesus as the Messiah when He was presented as a baby (Luke 2:36-38).',
      emoji: '📜'
    },
    'Disciple': {
      name: 'Disciple',
      tierColor: '#8B5CF6',
      openingQuote: 'You have committed your life to learning from Jesus and being transformed into His likeness.',
      mainDescription: [
        'As a Disciple, you understand that following Jesus is a lifelong process of learning and growing. You embrace His call: "Come, follow me, and I will make you fishers of men" (Matthew 4:19). Your identity is found in being His student and apprentice.',
        'Your knowledge of Scripture has reached a level where you can teach others and help them grow in their faith. Like the disciples who went from confused followers to bold proclaimers of the Gospel, you are being transformed by the truths you have learned.'
      ],
      biblicalExampleTitle: 'John, the beloved disciple',
      biblicalExampleDescription: 'Who learned so deeply from Jesus that his letters overflow with love and his Gospel reveals Jesus\' divine nature.',
      emoji: '✝️'
    },
    'Apostle': {
      name: 'Apostle',
      tierColor: '#8B5CF6',
      openingQuote: 'You have been sent out to carry the Gospel message and establish others in the faith.',
      mainDescription: [
        'As an Apostle, you understand that this title means "sent one." Like the original twelve and Paul, you have been commissioned to plant churches, establish believers, and spread the Gospel. Your deep knowledge of Scripture enables you to lay strong foundations wherever God sends you.',
        'You carry the authority of one who has been with Jesus and learned from Him. Your ministry is marked by signs, wonders, and most importantly, lives transformed by the Gospel. You understand that apostolic ministry is about building God\'s kingdom, not your own.'
      ],
      biblicalExampleTitle: 'Paul',
      biblicalExampleDescription: 'Who was called to be an apostle to the Gentiles and planted churches throughout the Roman Empire while enduring great hardship.',
      emoji: '⛪'
    },
    'Evangelist': {
      name: 'Evangelist',
      tierColor: '#F59E0B',
      openingQuote: 'Your heart burns with passion to share the good news of Jesus Christ with those who have never heard.',
      mainDescription: [
        'As an Evangelist, you have the gift of proclaiming the Gospel in ways that penetrate hearts and minds. You understand that "faith comes by hearing, and hearing by the word of God" (Romans 10:17), and you are equipped to present God\'s truth clearly and persuasively.',
        'Your extensive knowledge of Scripture allows you to answer questions, overcome objections, and guide people to faith in Christ. Like Philip who explained the Gospel to the Ethiopian eunuch, you have the ability to take complex biblical truths and make them understandable to anyone.'
      ],
      biblicalExampleTitle: 'Philip the evangelist',
      biblicalExampleDescription: 'Who preached in Samaria with great success and personally led the Ethiopian eunuch to faith (Acts 8).',
      emoji: '📢'
    },
    'Teacher': {
      name: 'Teacher',
      tierColor: '#F59E0B',
      openingQuote: 'God has given you the gift to explain His Word and help others grow in their understanding of biblical truth.',
      mainDescription: [
        'As a Teacher, you follow in the footsteps of Jesus, who was often called "Rabbi" or "Teacher." You have the ability to take complex spiritual truths and break them down into understandable principles that help people grow in their faith.',
        'Your deep study of Scripture has given you the tools to "rightly divide the word of truth" (2 Timothy 2:15). You understand that teaching is not just about transferring information but about transformation. Like Apollos, who was "mighty in the Scriptures," you use your knowledge to build up the body of Christ.'
      ],
      biblicalExampleTitle: 'Ezra the scribe',
      biblicalExampleDescription: 'Who "had set his heart to study the Law of the Lord, and to do it and to teach his statutes and rules in Israel" (Ezra 7:10).',
      emoji: '👨‍🏫'
    },
    'Theologian': {
      name: 'Theologian',
      tierColor: '#F59E0B',
      openingQuote: 'You have developed a systematic understanding of God\'s nature and His relationship with humanity through careful study of Scripture.',
      mainDescription: [
        'As a Theologian, you can articulate the deeper truths of the faith and help others understand the "why" behind biblical doctrines. You have studied not just what the Bible says, but how all its teachings fit together into a coherent understanding of God\'s character and His plan.',
        'Your knowledge spans both Testaments, and you can trace themes from Genesis to Revelation. Like the lawyer who asked Jesus about the greatest commandment, you seek to understand the heart of God\'s law and how it applies to life and faith.'
      ],
      biblicalExampleTitle: 'Luke, the beloved physician',
      biblicalExampleDescription: 'Who carefully investigated and recorded the life of Jesus and the early church with theological precision.',
      emoji: '📚'
    },
    'Scholar': {
      name: 'Scholar',
      tierColor: '#FFD700',
      openingQuote: 'Your dedication to rigorous study has given you mastery over the Scriptures that few achieve.',
      mainDescription: [
        'As a Scholar, you have committed yourself to the kind of detailed study that Paul commended in Timothy: "Study to show yourself approved unto God, a workman that needs not to be ashamed, rightly dividing the word of truth" (2 Timothy 2:15).',
        'Your knowledge encompasses the historical context, original languages, and cultural background of biblical texts. You can trace theological concepts across Scripture and explain difficult passages that challenge others. Your scholarship serves the church by preserving and teaching biblical truth.'
      ],
      biblicalExampleTitle: 'Paul himself',
      biblicalExampleDescription: 'Who was educated under Gamaliel and could quote Scripture extensively while reasoning with both Jews and Gentiles (Acts 22:3).',
      emoji: '🎓'
    },
    'Sage': {
      name: 'Sage',
      tierColor: '#FFD700',
      openingQuote: 'Wisdom flows from your understanding of God\'s Word, and others seek your counsel in matters of faith and life.',
      mainDescription: [
        'As a Sage, you embody the wisdom literature of Scripture. Like Solomon, you understand that "the fear of the Lord is the beginning of wisdom" (Proverbs 9:10), and your life demonstrates the practical application of biblical truth.',
        'Your knowledge has matured into wisdom. You can discern not just what Scripture says, but how to apply it wisely in complex life situations. People seek you out because you have learned to "speak a word in season to him who is weary" (Isaiah 50:4).'
      ],
      biblicalExampleTitle: 'Gamaliel',
      biblicalExampleDescription: 'The wise teacher who counseled the Sanhedrin to be careful in their treatment of the apostles (Acts 5:34-39).',
      emoji: '🧙‍♂️'
    },
    'Biblical Master': {
      name: 'Biblical Master',
      tierColor: '#FFD700',
      openingQuote: 'You have achieved a rare level of biblical mastery that comes only through years of dedicated study and faithful application.',
      mainDescription: [
        'As a Biblical Master, you have reached the pinnacle of biblical knowledge and wisdom. Your understanding spans the entirety of Scripture, from the most familiar passages to the most obscure prophecies. You can navigate complex theological questions and provide insight that helps others grow in their faith.',
        'Like Ezra who was "a ready scribe in the law of Moses" and had "prepared his heart to seek the law of the Lord, and to do it, and to teach in Israel statutes and judgments" (Ezra 7:6, 10), you have dedicated your life to knowing and living God\'s Word.',
        'Your mastery is not for personal glory but for the building up of God\'s kingdom. You understand that "to whom much is given, much will be required" (Luke 12:48), and you use your knowledge to serve others and honor God.'
      ],
      biblicalExampleTitle: 'Jesus Christ Himself',
      biblicalExampleDescription: 'Who demonstrated perfect knowledge of Scripture and fulfilled every prophecy written about Him (Luke 24:27).',
      emoji: '👑'
    }
  };
  
  return loreMap[rankName] || loreMap['Seeker'];
};

// Get rank by points
export const getRankByPoints = (points: number): BiblicalRank => {
  const ranks = [
    { level: 1, name: 'Seeker', pointsRequired: 0, tier: 'foundation' },
    { level: 2, name: 'Believer', pointsRequired: 500, tier: 'foundation' },
    { level: 3, name: 'Follower', pointsRequired: 1000, tier: 'foundation' },
    { level: 4, name: 'Servant', pointsRequired: 2000, tier: 'growth' },
    { level: 5, name: 'Shepherd', pointsRequired: 3500, tier: 'growth' },
    { level: 6, name: 'Elder', pointsRequired: 5000, tier: 'growth' },
    { level: 7, name: 'Prophet', pointsRequired: 6500, tier: 'wisdom' },
    { level: 8, name: 'Disciple', pointsRequired: 8000, tier: 'wisdom' },
    { level: 9, name: 'Apostle', pointsRequired: 10000, tier: 'wisdom' },
    { level: 10, name: 'Evangelist', pointsRequired: 12500, tier: 'mastery' },
    { level: 11, name: 'Teacher', pointsRequired: 15000, tier: 'mastery' },
    { level: 12, name: 'Theologian', pointsRequired: 17500, tier: 'mastery' },
    { level: 13, name: 'Scholar', pointsRequired: 20000, tier: 'transcendence' },
    { level: 14, name: 'Sage', pointsRequired: 25000, tier: 'transcendence' },
    { level: 15, name: 'Biblical Master', pointsRequired: 30000, tier: 'transcendence' }
  ];
  
  // Find the highest rank the user has achieved
  let currentRank = ranks[0];
  for (const rank of ranks) {
    if (points >= rank.pointsRequired) {
      currentRank = rank;
    } else {
      break;
    }
  }
  
  return currentRank;
};

const ranksService = {
  // Get all biblical ranks
  getBiblicalRanks: async (): Promise<BiblicalRank[]> => {
    try {
      const response = await usersApiClient.get('/ranks');
      return response.data;
    } catch (error) {
      console.error('Failed to get biblical ranks:', error);
      throw error;
    }
  },
  
  // Get rank by points (client-side calculation)
  getRankByPoints,
  
  // Get rank image path
  getRankImagePath,
  
  // Get rank emoji
  getRankEmoji,
  
  // Get tier color
  getTierColor,
  
  // Get rank lore
  getRankLore
};

export const getRankProgress = (totalPoints: number | undefined): RankProgress => {
  // Handle undefined totalPoints by defaulting to 0
  const points = totalPoints || 0;
  const currentRank = getRankByPoints(points);
  
  // Use the same ranks array as getRankByPoints
  const ranks = [
    { level: 1, name: 'Seeker', pointsRequired: 0, tier: 'foundation' },
    { level: 2, name: 'Believer', pointsRequired: 500, tier: 'foundation' },
    { level: 3, name: 'Follower', pointsRequired: 1000, tier: 'foundation' },
    { level: 4, name: 'Servant', pointsRequired: 2000, tier: 'growth' },
    { level: 5, name: 'Shepherd', pointsRequired: 3500, tier: 'growth' },
    { level: 6, name: 'Elder', pointsRequired: 5000, tier: 'growth' },
    { level: 7, name: 'Prophet', pointsRequired: 6500, tier: 'wisdom' },
    { level: 8, name: 'Disciple', pointsRequired: 8000, tier: 'wisdom' },
    { level: 9, name: 'Apostle', pointsRequired: 10000, tier: 'wisdom' },
    { level: 10, name: 'Evangelist', pointsRequired: 12500, tier: 'mastery' },
    { level: 11, name: 'Teacher', pointsRequired: 15000, tier: 'mastery' },
    { level: 12, name: 'Theologian', pointsRequired: 17500, tier: 'mastery' },
    { level: 13, name: 'Scholar', pointsRequired: 20000, tier: 'transcendence' },
    { level: 14, name: 'Sage', pointsRequired: 25000, tier: 'transcendence' },
    { level: 15, name: 'Biblical Master', pointsRequired: 30000, tier: 'transcendence' }
  ];
  
  // Find the next rank by looking for the first rank with higher point requirement
  const nextRank = ranks.find(rank => rank.pointsRequired > points) || null;
  
  // Calculate progress to next rank
  let progressPercentage = 100;
  let progressText = `${points.toLocaleString()} points earned (Max Rank Achieved!)`;
  let pointsToNextRank = 0;
  let pointsIntoCurrentRank = points - currentRank.pointsRequired;
  const isMaxRank = !nextRank;
  
  if (nextRank) {
    const currentRankPoints = currentRank.pointsRequired;
    const nextRankPoints = nextRank.pointsRequired;
    const pointsNeededForNextRank = nextRankPoints - currentRankPoints;
    
    progressPercentage = (pointsIntoCurrentRank / pointsNeededForNextRank) * 100;
    progressText = `${points.toLocaleString()} / ${nextRank.pointsRequired.toLocaleString()} points to ${nextRank.name}`;
    pointsToNextRank = nextRank.pointsRequired - points;
  }
  
  return {
    currentRank,
    nextRank,
    progressPercentage: Math.min(progressPercentage, 100),
    progressText,
    pointsToNextRank,
    pointsIntoCurrentRank,
    isMaxRank
  };
};

export default ranksService;